import React, { useState } from "react";
import {
  Calendar,
  FileText,
  DollarSign,
  TrendingUp,
  Plus,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building,
  HelpCircle,
  Save,
  Menu,
  X,
  MapPin,
  Percent,
  Calculator,
  Download,
  Eye,
  Archive,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button } from "../../components/ui";
import { Link } from "react-router-dom";
import JobStatusCard from "../../components/filing/JobStatusCard";
import { getTrackedJobIds } from "../../lib/triageJobsStorage";

const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [trackedJobIds] = useState(() => getTrackedJobIds());

  // Mock data - will be replaced with real API calls
  const dashboardData = {
    upcomingDeadline: {
      date: "April 30, 2024",
      jurisdiction: "Lead County, NO",
      daysLeft: 38,
      filingType: "Business Personal Property Tax",
    },
    recentFilings: {
      totalValue: "$25,752",
      description: "Total Assessed Value",
      count: 3,
      status: "Filed",
    },
    estimatedSavings: {
      percentage: "8-12%",
      description: "Contacted to Typedit DPA Trina",
      dollarAmount: "$1,200 - $1,800",
      comparedTo: "vs. Traditional CPA",
    },
  };

  const recentFilings = [
    {
      id: 1,
      jurisdiction: "Lead County, NO",
      filingYear: "2024",
      assessedValue: "$25,752",
      status: "Filed",
      dueDate: "2024-04-30",
      filedDate: "2024-03-15",
    },
    {
      id: 2,
      jurisdiction: "Harris County, TX",
      filingYear: "2024",
      assessedValue: "$18,945",
      status: "In Progress",
      dueDate: "2024-05-31",
      filedDate: null,
    },
    {
      id: 3,
      jurisdiction: "King County, WA",
      filingYear: "2023",
      assessedValue: "$32,150",
      status: "Filed",
      dueDate: "2023-04-30",
      filedDate: "2023-04-20",
    },
  ];

  const savedFilings = [
    {
      id: 1,
      name: "2024 BPPT - Lead County Draft",
      lastModified: "2024-01-15",
      progress: 75,
    },
    {
      id: 2,
      name: "2024 BPPT - Harris County",
      lastModified: "2024-01-10",
      progress: 40,
    },
  ];

  const sidebarItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: Building,
      path: "/presto",
      active: true,
    },
    {
      id: "new-filing",
      label: "Start New Filing",
      icon: Plus,
      path: "/filing/start",
      active: false,
    },
    {
      id: "saved",
      label: "Saved Filings",
      icon: Save,
      path: "/saved-filings",
      active: false,
    },
    {
      id: "help",
      label: "Help Center",
      icon: HelpCircle,
      path: "/help",
      active: false,
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Filed":
        return "bg-success/10 text-success border-success/20";
      case "In Progress":
        return "bg-warning/10 text-warning border-warning/20";
      case "Draft":
        return "bg-info/10 text-info border-info/20";
      default:
        return "bg-neutral-100 text-neutral-600 border-neutral-200";
    }
  };

  return (
    <div className="flex h-screen bg-neutral-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-primary transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}
      >
        <div className="flex items-center justify-between h-16 px-6 bg-primary-dark">
          <h2 className="text-lg font-semibold text-white">SolvTax</h2>
          <button onClick={() => setSidebarOpen(false)} className="text-white lg:hidden">
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-8 px-4">
          {sidebarItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`
                flex items-center gap-3 px-4 py-3 mb-2 rounded-lg text-sm font-medium transition-colors
                ${
                  item.active
                    ? "bg-white/10 text-white border-l-4 border-secondary"
                    : "text-primary-100 hover:bg-white/5 hover:text-white"
                }
              `}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-4 right-4">
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-primary-100 text-sm mb-2">Need Help?</p>
            <Link to="/help">
              <Button variant="secondary" size="sm" className="w-full">
                Contact Support
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-neutral-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="text-neutral-500 lg:hidden">
                <Menu className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900">Dashboard</h1>
                <p className="text-neutral-600">Manage your business property tax filings</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/filing/start">
                <Button variant="cta" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Start New Filing
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Main Dashboard Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {/* Live Filing Tracker: real jobs submitted via the Filing Wizard,
              polled from solvtax-api. Everything below this remains sample
              data until a "list jobs by business" endpoint exists. */}
          {trackedJobIds.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">Live Filing Status</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {trackedJobIds.map((jobId) => (
                  <JobStatusCard key={jobId} jobId={jobId} />
                ))}
              </div>
            </div>
          )}

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Upcoming Deadlines */}
            <Card className="border-l-4 border-l-warning">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-neutral-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Upcoming Deadlines
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-neutral-900 mb-1">
                  {dashboardData.upcomingDeadline.date}
                </div>
                <p className="text-sm text-neutral-600 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {dashboardData.upcomingDeadline.jurisdiction}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                    {dashboardData.upcomingDeadline.daysLeft} days left
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Recent Filings */}
            <Card className="border-l-4 border-l-success">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-neutral-600 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Recent Filings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-neutral-900 mb-1">
                  {dashboardData.recentFilings.totalValue}
                </div>
                <p className="text-sm text-neutral-600">
                  {dashboardData.recentFilings.description}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                    {dashboardData.recentFilings.count} filings completed
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Estimated Tax Savings */}
            <Card className="border-l-4 border-l-info">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-neutral-600 flex items-center gap-2">
                  <Percent className="h-4 w-4" />
                  Estimated Tax Savings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-info mb-1">
                  {dashboardData.estimatedSavings.percentage}
                </div>
                <p className="text-sm text-neutral-600">
                  {dashboardData.estimatedSavings.description}
                </p>
                <div className="mt-3">
                  <span className="text-xs text-neutral-500">
                    {dashboardData.estimatedSavings.dollarAmount}{" "}
                    {dashboardData.estimatedSavings.comparedTo}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Filings Table */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Recent Filings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentFilings.map((filing) => (
                    <div
                      key={filing.id}
                      className="border rounded-lg p-4 hover:bg-neutral-50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-neutral-900">{filing.jurisdiction}</h4>
                          <p className="text-sm text-neutral-600">Tax Year {filing.filingYear}</p>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            filing.status
                          )}`}
                        >
                          {filing.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-600">Assessed Value:</span>
                        <span className="font-medium">{filing.assessedValue}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-neutral-600">Due Date:</span>
                        <span>{filing.dueDate}</span>
                      </div>
                      {filing.status === "Filed" && (
                        <div className="flex items-center gap-2 mt-3">
                          <Button variant="outline" size="sm" className="gap-1">
                            <Eye className="h-3 w-3" />
                            View
                          </Button>
                          <Button variant="ghost" size="sm" className="gap-1">
                            <Download className="h-3 w-3" />
                            Download
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Saved Filings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Save className="h-5 w-5" />
                  Saved Filings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {savedFilings.map((filing) => (
                    <div
                      key={filing.id}
                      className="border rounded-lg p-4 hover:bg-neutral-50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-neutral-900">{filing.name}</h4>
                          <p className="text-sm text-neutral-600">
                            Last modified: {filing.lastModified}
                          </p>
                        </div>
                        <span className="text-sm font-medium text-neutral-600">
                          {filing.progress}%
                        </span>
                      </div>
                      <div className="w-full bg-neutral-200 rounded-full h-2 mb-3">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${filing.progress}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="gap-1">
                          Continue Filing
                        </Button>
                        <Button variant="ghost" size="sm" className="gap-1">
                          <Archive className="h-3 w-3" />
                          Archive
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <Link to="/filing/start">
                    <Button variant="outline" className="w-full gap-2">
                      <Plus className="h-4 w-4" />
                      Create New Filing
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-6 text-center">
                <Calculator className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium text-neutral-900 mb-2">Tax Calculator</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Estimate your property tax liability
                </p>
                <Button variant="outline" size="sm">
                  Launch Calculator
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-6 text-center">
                <HelpCircle className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium text-neutral-900 mb-2">Get Help</h3>
                <p className="text-sm text-neutral-600 mb-4">Connect with our tax experts</p>
                <Button variant="outline" size="sm">
                  Contact Support
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-6 text-center">
                <FileText className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium text-neutral-900 mb-2">Document Library</h3>
                <p className="text-sm text-neutral-600 mb-4">Access all your filing documents</p>
                <Button variant="outline" size="sm">
                  View Documents
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
